import { Injectable, Injector, inject } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { RoutingService } from '@skylab-angular-shared/core';
import { CommonKeys, DialogInsService } from '@skylab-angular-shared/ui';
import { BusinessObjectBase, ConsoleLogger, EventBusBase, L10nBase, LocalContext, ModelBase, RepositoryBase, RepositoryResponseCode, RepositoryResponseConflictCodes, RepositorySaveResponse, RootContextBase, SaveConflictResponse, buildValidationKey } from '@skylab-shared/core';
import { ListenersRegistry, head } from '@skylab-shared/utils';
import { BehaviorSubject, Subscription } from 'rxjs';
import { RootContainerOverlayOptions, RootContainerOverlayService } from '../common/root-container-overlay.service';
import { FormState } from './form-state';
import { DataNotFoundContext } from './form-state-handlers/form-state-data-not-found-error.service';
import { SuccessContext } from './form-state-handlers/form-state-success.service';
import { ValidationErrorContext } from './form-state-handlers/form-state-validation-error.service';
import { FormStateManager } from './form-state-manager.service';
import { ViewValidation, ViewValidationService } from './form-validation';
import { FormValidatorBase } from './form-validator-base';

export type FormBaseViewModel = {};

@Injectable({ providedIn: 'root' })
export abstract class FormBase<
    TViewModel extends FormBaseViewModel,
    TRepository extends RepositoryBase<TBo, TModel>,
    TBo extends BusinessObjectBase<TModel>,
    TModel extends ModelBase>
    extends ListenersRegistry(class { }) {
    protected readonly localContext: LocalContext;
    protected readonly logger: ConsoleLogger;

    public bo!: TBo;
    public isFormReady: boolean;
    public viewModel!: TViewModel;

    private stateParamsSubscription!: Subscription;

    protected showAllValidations = new BehaviorSubject(false);
    private formValidators!: FormValidatorBase[];

    protected navigationExtras: NavigationExtras;

    private readonly viewValidationService = inject(ViewValidationService);
    public isFormDirty = new BehaviorSubject(false);

    protected readonly injector = inject(Injector);

    public constructor(
        protected readonly rootContext: RootContextBase,
        protected readonly rootContainerOverlayService: RootContainerOverlayService,
        protected readonly stateManager: FormStateManager,
        protected readonly mainRepository: TRepository,
        protected readonly dialogService: DialogInsService,
        protected readonly l10n: L10nBase,
        protected readonly routingService: RoutingService,
        protected readonly eventBus: EventBusBase
    ) {
        super();
        this.logger = new ConsoleLogger(this.constructor.name);
        this.localContext = rootContext.createLocalContext();

        this.isFormReady = false;
        this.navigationExtras = this.routingService.getExtrasFromPreviousState() || {};
    }

    public viewValidation!: ViewValidation;

    // eslint-disable-next-line @angular-eslint/contextual-lifecycle
    public async ngOnInit(): Promise<void> {
        try {
            this.stateManager.setState(FormState.None);
            this.rootContainerOverlayService.show();
            await this.preloadDataAsync();
            this.bo = await this.prepareBoAsync();
            this.bo.attachReadonlyChecker();
            this.bo.queueRulesValidation();
            this.showAllValidations.next(!!this.inEditMode());
            await this.bo.waitForProcessingCompletionAsync();
            this.viewModel = await this.prepareFormAsync();
            this.formValidators = this.getFormValidators()?.filter(Boolean) || [];
            this.viewValidation = this.viewValidationService.create([this.bo, ...(this.formValidators ? this.formValidators.map(elem => elem.source) : [])], this.showAllValidations);
            this.updateDocumentTitle();
            this.bindKeysActions();
            this.formReady();
            this.handleBeforeUnload();
        } catch (err) {
            this.logger.error(err);
        } finally {
            this.rootContainerOverlayService.hide();
        }
    }

    // eslint-disable-next-line @angular-eslint/contextual-lifecycle
    public ngAfterViewInit(): void {
        this.stateParamsSubscription = this.routingService.currentStateParams$.subscribe(async data => {
            const viewKindChanged = data.curr?.['kind'] !== data.prev?.['kind'];
            if (!viewKindChanged) return;
            await this.routingService.reloadCurrentState();
        });
    }

    protected ngOnDestroy(): void {
        this.stateParamsSubscription?.unsubscribe();
        this.formValidators?.forEach(elem => elem?.dispose());
        this.viewValidation?.dispose();
        this.deregisterAllListeners();

        this.bo?.dispose();
        this.localContext?.dispose();
        this.stateManager.setState(FormState.None);
    }

    protected formReady(): void {
        this.isFormReady = true;
    }

    private handleBeforeUnload(): void {
        const handle = (event: BeforeUnloadEvent) => {
            if (!this.isFormDirty.value) return false;
            event.returnValue = this.l10n.instant('scenes.shared.form.beforeunload');
            return this.l10n.instant('scenes.shared.form.beforeunload');
        };

        window.addEventListener('beforeunload', handle);
        this.registerListening(() => {
            window.removeEventListener('beforeunload', handle);
        });
    }

    protected preloadDataAsync(): Promise<void> {
        return Promise.resolve();
    }

    protected prepareFormAsync(): Promise<TViewModel> {
        const viewModel = <TViewModel>{};
        return Promise.resolve(viewModel);
    }

    private async loadBoAsync(): Promise<TBo> {
        try {
            return await this.mainRepository.loadAsync(this.documentToLoadProperties, this.localContext);
        } catch (err) {
            this.logger.error(err);
            this.stateManager.setState(FormState.DataNotFound, this.dataNotFoundContext);
            throw err;
        }
    }

    private async createBoAndReplicateAsync(): Promise<TBo> {
        try {
            const model = await this.mainRepository.findAsync(this.documentToReplicateProperties);
            const bo = await this.mainRepository.createNewAsync(this.localContext);
            await this.replicateAsync(model, bo.model, bo);
            return bo;
        } catch (response) {
            this.logger.error(response);
            this.stateManager.setState(FormState.DataNotFound, this.dataNotFoundContext);
            throw response;
        }
    }

    protected get documentToLoadProperties(): {id: number; [key: string]: string | number} {
        return { id: +(this.stateParams?.['id'] ?? 0) };
    }

    protected get documentToReplicateProperties(): {id: number; [key: string]: string | number} {
        return { id: +(this.stateParams?.['replicateId'] ?? 0) };
    }

    protected prepareBoAsync(): Promise<TBo> {
        if (this.inEditMode()) {
            return this.loadBoAsync();
        } else if (this.inReplicateMode()) {
            return this.createBoAndReplicateAsync();
        }
        return this.mainRepository.createNewAsync(this.localContext);
    }

    public saving(): boolean {
        return this.stateManager.state === FormState.Saving;
    }

    public inEditMode(): boolean {
        return !!this.stateParams?.['id'];
    }

    protected inReplicateMode(): boolean {
        return !!this.stateParams?.['replicateId'];
    }

    public isNew(): boolean {
        return !this.inEditMode() && !this.inReplicateMode();
    }

    public get isFormSubmitted(): boolean {
        return this.stateManager.state !== FormState.None;
    }

    public get isSaveRequested(): boolean {
        return this.stateManager.state === FormState.SaveRequested;
    }

    private bindKeysActions(): void {
        const unsub = CommonKeys.add({ ctrlEnter: this.saveAndRedirectAsync.bind(this), esc: this.cancel.bind(this) });
        if (unsub) {
            this.registerListening(unsub);
        }
    }

    protected async canSaveAsync(): Promise<boolean> {
        return true;
    }

    public async reloadState(): Promise<void> {
        this.isFormDirty.next(false);
        this.routingService.reloadCurrentState();
    }

    public async saveAndRedirectAsync(): Promise<void> {
        if ([FormState.SaveRequested, FormState.Saving].includes(this.stateManager.state)) return;

        const result = await this.saveAsync();
        if (result?.code !== RepositoryResponseCode.Success) return;

        this.redirectToPreviousState(result);
    }

    protected redirectToPreviousState(data: unknown): void {
        this.routingService.navigate([this.previousState.url], { queryParams: this.previousState.params});
    }

    public async saveAsync(): Promise<RepositorySaveResponse | null> {
        this.showAllValidations.next(true);
        this.stateManager.setState(FormState.SaveRequested);
        await this.bo.waitForProcessingCompletionAsync();

        if (!this.bo.isValid || !this.areFormValidatorsValid) {
            this.stateManager.setState(FormState.ValidationError, this.getValidationErrorContext());
            return null;
        } else if (await this.canSaveAsync()) {
            this.rootContainerOverlayService.show(this.getSaveFormLoadingOverlayText());
            try {
                this.stateManager.setState(FormState.Saving);
                const result = await this.mainRepository.saveAsync(this.bo);

                if (result.code === RepositoryResponseCode.Success) {
                    this.isFormDirty.next(false);
                    this.stateManager.setState(FormState.Success, this.getSuccessContext(result));
                }

                if (result.code === RepositoryResponseCode.ValidationError) {
                    this.stateManager.setState(FormState.ValidationError, this.getValidationErrorContext(result));
                }

                if (result.code === RepositoryResponseCode.Conflict) {
                    this.handleConflictSaveResponse(result);
                }

                if (result.code === RepositoryResponseCode.OutOfDateData) {
                    this.stateManager.setState(FormState.DataHasChangedError, {
                        reloadAction: async () => this.reloadState()
                    });
                }

                if (result.code === RepositoryResponseCode.DataNotFound) {
                    this.isFormDirty.next(false);
                    this.stateManager.setState(FormState.DataNotFound, this.dataNotFoundContext);
                }

                if (result.code === RepositoryResponseCode.ExternalError) {
                    this.stateManager.setState(FormState.ExternalError);
                }

                return result;
            } catch (err) {
                this.logger.error(err);
                this.stateManager.setState(FormState.ExternalError);

                return null;
            } finally {
                this.rootContainerOverlayService.hide();
            }
        } else {
            this.stateManager.setState(FormState.ProcessError);
            return null;
        }
    }

    protected handleConflictSaveResponse(result: SaveConflictResponse<unknown>): void {
        const alreadyCreatedConflict = result.conflicts?.find(elem => elem.code === RepositoryResponseConflictCodes.EntityAlreadyCreated);
        if (alreadyCreatedConflict) {
            const createdObject = (head(alreadyCreatedConflict.objectProperties || []) as {id: number, documentNumber: string});

            this.isFormDirty.next(false);
            this.stateManager.setState(FormState.Success, this.getSuccessContext({object: createdObject}));
            this.redirectToPreviousState({object: createdObject});
        } else {
            this.stateManager.setState(FormState.ValidationError, this.getValidationErrorContext(result));
        }
    }

    protected get previousState(): { url: string, params: { [param: string]: any } } {
        const states = this.routingService.appStates;

        const prev = this.routingService.previousState;
        const curr = this.routingService.currentState;

        const prevState = states.find(elem => elem.url === prev?.url);
        const currState = states.find(elem => elem.url === curr.url);

        return prev
            ? {
                url: prevState?.parent ? prevState.parent.url : prev.url,
                params: this.routingService.previousState?.params || {}
            }
            : {
                url: currState?.parent?.url || '',
                params: {}
            };
    }

    public getValidationKey(propertyName: string, modelSun?: string): string | undefined {
        if (this.bo && this.bo.model) {
            modelSun = modelSun || this.bo.model.sun;
            return buildValidationKey(modelSun, propertyName);
        }
        return undefined;
    }

    private get areFormValidatorsValid(): boolean {
        return this.formValidators?.every(elem => elem.isValid()) ?? true;
    }

    public cancel(): void {
        let params = this.previousState.params;
        if (this.stateParams?.['id']) params = {...params, lastTouchedId: this.stateParams['id']};
        this.routingService.navigate([this.previousState.url], { queryParams: params});
    }

    protected get stateParams() {
        return this.routingService.currentState.params;
    }

    protected abstract replicateAsync(source: TModel, target: TModel, bo: TBo): Promise<void>;

    protected abstract get dataNotFoundContext(): DataNotFoundContext;
    protected abstract getValidationErrorContext(data?: RepositorySaveResponse): ValidationErrorContext;
    protected abstract getSuccessContext(data?: unknown): SuccessContext;

    protected abstract updateDocumentTitle(): void;
    protected getSaveFormLoadingOverlayText(): RootContainerOverlayOptions | undefined {
        return undefined;
    };

    protected getFormValidators(): FormValidatorBase[] | null {
        return null;
    };

    protected onExit_confirm(): void { }
    protected onExit_decline(): void { }
    protected onExit_cancel(): void { }
}
